import {
  getFieldsForTemplate,
  type ArtworkPrintSettings,
  type PrintTemplate,
} from "@/components/artwork/artworkPrintSettings";
import { useCallback } from "react";
import { useFieldPresenceContext } from "./field-presence.context";

/**
 * Le point d'insertion **unique** du masquage par présence.
 *
 * Les drawers d'impression rendent leurs cases depuis neuf appels à
 * `getFieldsForTemplate`, chacun avec sa propre chaîne de filtres (visibilité
 * de la fiche, visibilité d'onglet). Passer par ce hook plutôt que d'ajouter un
 * `.filter(...)` à chacun évite qu'un dixième appel, écrit plus tard, échappe
 * silencieusement au filtrage.
 *
 * Les filtres propres à chaque drawer restent chaînés en aval : la présence ne
 * les remplace pas, elle s'y ajoute et ne peut que masquer davantage.
 */
export function usePresentPrintFields() {
  const { isPresent } = useFieldPresenceContext();

  /** Champs d'un groupe pour ce modèle, privés de ceux qui n'ont pas de valeur. */
  const fieldsForGroup = useCallback(
    (template: PrintTemplate, groupKey: string): readonly string[] =>
      getFieldsForTemplate(template, groupKey).filter((field) =>
        isPresent(field),
      ),
    [isPresent],
  );

  /** Prédicat brut, pour les listes qui ne passent pas par un groupe. */
  const keepPresent = useCallback(
    (field: string | keyof ArtworkPrintSettings): boolean =>
      isPresent(field as string),
    [isPresent],
  );

  return { fieldsForGroup, keepPresent };
}
